import type { ComponentProps } from 'react';
import {
  PageBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import type { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  Direction,
  catalogGraphRouteRef,
} from '@backstage/plugin-catalog-graph';
import { z } from 'zod/v4';

/**
 * Overrides the catalog-graph page extension to work around a prop-shape
 * mismatch in @backstage/plugin-catalog-graph 0.6.5.
 *
 * The plugin's own page extension spreads its config as flat props
 * (`<CatalogGraphPage {...config} />`), but CatalogGraphPage reads its defaults
 * from a nested `initialState` prop. rootEntityRefs, selectedKinds, maxDepth
 * and direction are therefore silently dropped, leaving the graph with no root
 * entities to expand from and so nothing to render.
 *
 * This override keeps the upstream config schema and route, and only nests the
 * state keys where CatalogGraphPage actually looks for them. It can be deleted
 * once the plugin passes its config through correctly.
 */

const stateSchema = {
  rootEntityRefs: z.array(z.string()).optional(),
  selectedKinds: z.array(z.string()).optional(),
  selectedRelations: z.array(z.string()).optional(),
  maxDepth: z.number().optional(),
  unidirectional: z.boolean().optional(),
  mergeRelations: z.boolean().optional(),
  direction: z.nativeEnum(Direction).optional(),
  showFilters: z.boolean().optional(),
  curve: z.enum(['curveStepBefore', 'curveMonotoneX']).optional(),
};

const graphSchema = {
  showArrowHeads: z.boolean().optional(),
  kinds: z.array(z.string()).optional(),
  relations: z.array(z.string()).optional(),
  relationPairs: z.array(z.tuple([z.string(), z.string()])).optional(),
  zoom: z.enum(['enabled', 'disabled', 'enable-on-click']).optional(),
};

type PageConfig = { [K in keyof typeof stateSchema]?: unknown } & {
  [K in keyof typeof graphSchema]?: unknown;
};

/**
 * Splits extension config into the nested `initialState` the page reads its
 * defaults from, and the props forwarded to the underlying graph.
 */
export function toCatalogGraphPageProps(
  config: PageConfig,
): ComponentProps<typeof CatalogGraphPage> {
  const {
    showArrowHeads,
    kinds,
    relations,
    relationPairs,
    zoom,
    ...initialState
  } = config as ComponentProps<typeof CatalogGraphPage>['initialState'] &
    Record<string, any>;

  return {
    initialState,
    showArrowHeads,
    kinds,
    relations,
    relationPairs,
    zoom,
  };
}

export const catalogGraphPage = PageBlueprint.makeWithOverrides({
  configSchema: { ...stateSchema, ...graphSchema },
  factory(originalFactory, { config }) {
    const props = toCatalogGraphPageProps(config);

    return originalFactory({
      path: '/catalog-graph',
      routeRef: catalogGraphRouteRef,
      loader: () =>
        import('@backstage/plugin-catalog-graph').then(m => (
          <m.CatalogGraphPage {...props} />
        )),
    });
  },
});

export const catalogGraphModule = createFrontendModule({
  pluginId: 'catalog-graph',
  extensions: [catalogGraphPage],
});
